<app-loading *ngIf="!contentReady"></app-loading>

<div class="apply-page container-fluid" dir="rtl" *ngIf="contentReady">
  <section class="page-shell">
    <header class="apply-header">
      <div>
        <span class="eyebrow">
          <i class="fa-solid fa-file-signature"></i>
          Apply
        </span>
        <h1>تقديمات الوظائف</h1>
        <p>متابعة كل التقديمات القادمة من الموقع وربطها بالوظائف والمرشحين.</p>
      </div>

      <div class="header-actions">
        <button type="button" class="main-btn" routerLink="/alljobs">
          <i class="fa-solid fa-briefcase"></i>
          الوظائف
        </button>
        <button type="button" class="secondary-btn" (click)="refresh()">
          <i class="fa-solid fa-rotate-right"></i>
          تحديث
        </button>
      </div>
    </header>

    <div class="stats-grid">
      <article class="stat-card">
        <i class="fa-solid fa-layer-group"></i>
        <span>كل التقديمات</span>
        <strong>{{ toArabicNumber(stats.total) }}</strong>
      </article>

      <article class="stat-card pending">
        <i class="fa-solid fa-hourglass-half"></i>
        <span>قيد المتابعة في الصفحة</span>
        <strong>{{ toArabicNumber(stats.pending) }}</strong>
      </article>

      <article class="stat-card accepted">
        <i class="fa-solid fa-circle-check"></i>
        <span>مقبولين في الصفحة</span>
        <strong>{{ toArabicNumber(stats.accepted) }}</strong>
      </article>

      <article class="stat-card rejected">
        <i class="fa-solid fa-circle-xmark"></i>
        <span>مرفوضين في الصفحة</span>
        <strong>{{ toArabicNumber(stats.rejected) }}</strong>
      </article>
    </div>

    <div class="filters-panel">
      <div class="search-box">
        <i class="fa-solid fa-magnifying-glass"></i>
        <input
          type="text"
          [(ngModel)]="filters.search"
          (keyup.enter)="applyFilters()"
          [placeholder]="hasContactAccess ? 'ابحث باسم المرشح أو الوظيفة أو رقم الهاتف' : 'ابحث باسم المرشح أو الوظيفة'"
        />
      </div>

      <label class="filter-item">
        <span>الوظيفة</span>
        <select [(ngModel)]="filters.job_id" (change)="applyFilters()">
          <option value="">كل الوظائف</option>
          <option *ngFor="let job of jobs" [value]="job.id">
            {{ job.title_ar || job.title_en || job.title || ('وظيفة #' + job.id) }}
          </option>
        </select>
      </label>

      <label class="filter-item">
        <span>الحالة</span>
        <select [(ngModel)]="filters.status" (change)="applyFilters()">
          <option value="">كل الحالات</option>
          <option *ngFor="let status of statuses" [value]="status">
            {{ getStatusLabel(status) }}
          </option>
        </select>
      </label>

      <div class="filter-actions">
        <button type="button" class="main-btn" (click)="applyFilters()">بحث</button>
        <button type="button" class="clear-btn" (click)="resetFilters()">مسح</button>
      </div>
    </div>

    <div class="bulk-actions" *ngIf="selectedCount() > 0">
      <strong>{{ toArabicNumber(selectedCount()) }} تقديم محدد</strong>

      <div>
        <button type="button" class="secondary-btn" (click)="openBulkPopup()">
          <i class="fa-solid fa-layer-group"></i>
          تغيير حالة المحدد
        </button>
        <button type="button" class="clear-btn" (click)="clearSelection()">إلغاء التحديد</button>
      </div>
    </div>

    <div class="table-wrapper" *ngIf="!noData">
      <table mat-table [dataSource]="dataSource" class="apply-table">
        <ng-container matColumnDef="select">
          <th mat-header-cell *matHeaderCellDef>
            <mat-checkbox
              [checked]="isPageSelected()"
              [indeterminate]="isPagePartiallySelected()"
              (change)="togglePageSelection()"
              aria-label="تحديد كل التقديمات في الصفحة"
            ></mat-checkbox>
          </th>
          <td mat-cell *matCellDef="let application">
            <mat-checkbox
              [checked]="isApplicationSelected(application)"
              (change)="toggleApplicationSelection(application)"
              aria-label="تحديد التقديم"
            ></mat-checkbox>
          </td>
        </ng-container>

        <ng-container matColumnDef="applicant">
          <th mat-header-cell *matHeaderCellDef>المتقدم</th>
          <td mat-cell *matCellDef="let application">
            <div class="person-cell">
              <span class="avatar">
                {{ getApplicantName(application).charAt(0) }}
              </span>
              <div>
                <strong>{{ getApplicantName(application) }}</strong>
                <small>#{{ application.id || '-' }}</small>
              </div>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="job">
          <th mat-header-cell *matHeaderCellDef>الوظيفة</th>
          <td mat-cell *matCellDef="let application">
            <div class="job-cell">
              <strong>{{ getJobTitle(application) }}</strong>
              <small>{{ getJobLocation(application) }}</small>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="contact">
          <th mat-header-cell *matHeaderCellDef>التواصل</th>
          <td mat-cell *matCellDef="let application">
            <div class="contact-cell">
              <a [href]="'tel:' + getApplicantPhone(application)">
                <i class="fa-solid fa-phone"></i>
                {{ getApplicantPhone(application) }}
              </a>
              <a [href]="'mailto:' + getApplicantEmail(application)">
                <i class="fa-solid fa-envelope"></i>
                {{ getApplicantEmail(application) }}
              </a>
            </div>
          </td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef>الحالة</th>
          <td mat-cell *matCellDef="let application">
            <span class="status-pill" [ngClass]="getStatusClass(application.status)">
              {{ getStatusLabel(application.status) }}
            </span>
          </td>
        </ng-container>

        <ng-container matColumnDef="cv">
          <th mat-header-cell *matHeaderCellDef>السيرة الذاتية</th>
          <td mat-cell *matCellDef="let application">
            <a
              class="cv-link"
              *ngIf="getCvLink(application); else noCv"
              [href]="getCvLink(application)"
              target="_blank"
              rel="noopener"
            >
              <i class="fa-solid fa-file-arrow-down"></i>
              فتح CV
            </a>
            <ng-template #noCv>
              <span class="empty-text">لا يوجد</span>
            </ng-template>
          </td>
        </ng-container>

        <ng-container matColumnDef="created_at">
          <th mat-header-cell *matHeaderCellDef>تاريخ التقديم</th>
          <td mat-cell *matCellDef="let application">
            {{ formatDate(application.submitted_at || application.applied_at || application.created_at) }}
          </td>
        </ng-container>

        <ng-container matColumnDef="options">
          <th mat-header-cell *matHeaderCellDef></th>
          <td mat-cell *matCellDef="let application">
            <button
              class="options-btn"
              type="button"
              [disabled]="isUpdating"
              (click)="openActionPopup(application)"
              aria-label="فتح خيارات التقديم"
            >
              <i class="fa-solid fa-ellipsis-vertical"></i>
            </button>
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr
          mat-row
          *matRowDef="let row; columns: displayedColumns; trackBy: trackByApplicationId"
        ></tr>
      </table>

      <mat-paginator
        class="aes-paginator"
        [length]="totalItems"
        [pageSize]="perPage"
        [pageIndex]="currentPage - 1"
        [pageSizeOptions]="[10, 15, 25, 50]"
        [showFirstLastButtons]="true"
        (page)="onPaginateChange($event)"
      ></mat-paginator>
    </div>

    <div class="popup-backdrop" *ngIf="actionPopupApplication" (click)="closeActionPopup()">
      <section class="status-popup" (click)="$event.stopPropagation()" dir="rtl">
        <header>
          <div>
            <span>خيارات التقديم</span>
            <h2>{{ getApplicantName(actionPopupApplication) }}</h2>
            <p>{{ getJobTitle(actionPopupApplication) }}</p>
          </div>
          <button type="button" class="close-popup" (click)="closeActionPopup()" aria-label="إغلاق">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="popup-options-grid">
          <button
            type="button"
            class="popup-option"
            *ngFor="let status of statuses"
            [class.current-option]="getStatusClass(actionPopupApplication.status) === getStatusClass(status)"
            (click)="changeStatus(actionPopupApplication, status)"
          >
            <span class="option-icon" [ngClass]="getStatusClass(status)">
              <i class="fa-solid" [ngClass]="getStatusIcon(status)"></i>
            </span>
            <span>{{ getStatusLabel(status) }}</span>
            <i
              class="fa-solid fa-check current-check"
              *ngIf="getStatusClass(actionPopupApplication.status) === getStatusClass(status)"
            ></i>
          </button>
        </div>

        <footer>
          <button type="button" class="info-action" (click)="openJobPopup(actionPopupApplication)">
            <i class="fa-solid fa-briefcase"></i>
            فتح الوظيفة
          </button>

          <button
            type="button"
            class="success-action"
            [disabled]="isUpdating"
            (click)="convertApplicationToDoctor(actionPopupApplication)"
          >
            <i class="fa-solid fa-user-doctor"></i>
            تحويل لدكتور
          </button>

          <button
            type="button"
            class="primary-action"
            [disabled]="isUpdating"
            (click)="convertApplicationToKader(actionPopupApplication)"
          >
            <i class="fa-solid fa-user-tie"></i>
            تحويل لكادر
          </button>

          <button type="button" class="danger-action" (click)="deleteApplication(actionPopupApplication)">
            <i class="fa-solid fa-trash"></i>
            حذف التقديم
          </button>
        </footer>
      </section>
    </div>

    <div class="popup-backdrop" *ngIf="jobPopupApplication" (click)="closeJobPopup()">
      <section class="status-popup job-details-popup" (click)="$event.stopPropagation()" dir="rtl">
        <header>
          <div>
            <span>بيانات الوظيفة</span>
            <h2>{{ getJobTitle(jobPopupApplication) }}</h2>
            <p>{{ getJobLocation(jobPopupApplication) }}</p>
          </div>
          <div class="popup-header-actions">
            <button
              type="button"
              class="company-data-btn"
              (click)="openCompanyPopup(jobPopupApplication)"
            >
              <i class="fa-solid fa-building"></i>
              بيانات الشركة
            </button>

            <button type="button" class="close-popup" (click)="closeJobPopup()" aria-label="إغلاق">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
        </header>

        <div class="job-popup-scroll">
          <div class="job-details-grid">
            <article>
              <i class="fa-solid fa-briefcase"></i>
              <span>نوع العمل</span>
              <strong>{{ getJobType(jobPopupApplication) }}</strong>
            </article>

            <article>
              <i class="fa-solid fa-money-bill-wave"></i>
              <span>الراتب</span>
              <strong>{{ getJobSalary(jobPopupApplication) }}</strong>
            </article>

            <article>
              <i class="fa-solid fa-eye"></i>
              <span>المشاهدات</span>
              <strong>{{ toArabicNumber(getJobSummary(jobPopupApplication)?.views_count || 0) }}</strong>
            </article>

            <article>
              <i class="fa-solid fa-circle-info"></i>
              <span>الحالة</span>
              <strong>{{ getJobSummary(jobPopupApplication)?.status || 'غير محدد' }}</strong>
            </article>
          </div>

          <div class="job-description-box">
            <h3>الوصف</h3>
            <p>
              {{
                getJobSummary(jobPopupApplication)?.description_ar ||
                getJobSummary(jobPopupApplication)?.description_en ||
                getJobSummary(jobPopupApplication)?.description ||
                'لا يوجد وصف'
              }}
            </p>
          </div>

          <div class="job-description-box">
            <h3>المتطلابات </h3>
            <div class="details-chips" *ngIf="(getJobSummary(jobPopupApplication)?.requirements || []).length; else noRequirements">
              <span *ngFor="let req of getJobSummary(jobPopupApplication)?.requirements">
                {{ req.requirement_text || req.title || req.name || req }}
              </span>
            </div>
            <ng-template #noRequirements>
              <p>لا توجد متطلابات مسجلة</p>
            </ng-template>
          </div>

          <div class="job-description-box">
            <h3>المهارات</h3>
            <div class="details-chips" *ngIf="(getJobSummary(jobPopupApplication)?.skills || []).length; else noSkills">
              <span *ngFor="let skill of getJobSummary(jobPopupApplication)?.skills">
                {{ skill.name_ar || skill.name_en || skill.name || skill }}
              </span>
            </div>
            <ng-template #noSkills>
              <p>لا توجد مهارات مسجلة</p>
            </ng-template>
          </div>
        </div>
      </section>
    </div>

    <div class="popup-backdrop" *ngIf="companyPopupApplication" (click)="closeCompanyPopup()">
      <section class="status-popup company-popup" (click)="$event.stopPropagation()" dir="rtl">
        <header>
          <div>
            <span>بيانات الشركة</span>
            <h2>{{ getCompanyName(companyPopupApplication) }}</h2>
            <p>{{ getCompanySpecialty(companyPopupApplication) }}</p>
          </div>
          <button type="button" class="close-popup" (click)="closeCompanyPopup()" aria-label="إغلاق">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="company-empty-state" *ngIf="!hasCompanyData(companyPopupApplication)">
          <i class="fa-solid fa-building-circle-exclamation"></i>
          <h3>لا توجد بيانات شركة مسجلة</h3>
          <p>الوظيفة الحالية غير مرتبطة ببيانات شركة واضحة في رد السيرفر.</p>
        </div>

        <div class="company-details-grid" *ngIf="hasCompanyData(companyPopupApplication)">
          <article>
            <i class="fa-solid fa-location-dot"></i>
            <span>العنوان</span>
            <strong>{{ getCompanyAddress(companyPopupApplication) }}</strong>
          </article>

          <article *ngIf="hasContactAccess">
            <i class="fa-solid fa-phone"></i>
            <span>رقم الهاتف</span>
            <a [href]="'tel:' + getCompanyPhone(companyPopupApplication)">
              {{ getCompanyPhone(companyPopupApplication) }}
            </a>
          </article>

          <article *ngIf="hasContactAccess">
            <i class="fa-solid fa-envelope"></i>
            <span>الإيميل</span>
            <a [href]="'mailto:' + getCompanyEmail(companyPopupApplication)">
              {{ getCompanyEmail(companyPopupApplication) }}
            </a>
          </article>

          <article *ngIf="getCompanyWebsite(companyPopupApplication)">
            <i class="fa-solid fa-link"></i>
            <span>الرابط</span>
            <a [href]="getCompanyWebsite(companyPopupApplication)" target="_blank" rel="noopener">
              فتح الرابط
            </a>
          </article>
        </div>
      </section>
    </div>

    <div class="popup-backdrop" *ngIf="bulkPopupOpen" (click)="closeBulkPopup()">
      <section class="status-popup" (click)="$event.stopPropagation()" dir="rtl">
        <header>
          <div>
            <span>تغيير جماعي</span>
            <h2>{{ toArabicNumber(selectedCount()) }} تقديم محدد</h2>
            <p>اختار حالة واحدة لتطبيقها على كل التقديمات المحددة.</p>
          </div>
          <button type="button" class="close-popup" (click)="closeBulkPopup()" aria-label="إغلاق">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </header>

        <div class="popup-options-grid">
          <button
            type="button"
            class="popup-option"
            *ngFor="let status of statuses"
            [disabled]="isUpdating"
            (click)="changeBulkStatus(status)"
          >
            <span class="option-icon" [ngClass]="getStatusClass(status)">
              <i class="fa-solid" [ngClass]="getStatusIcon(status)"></i>
            </span>
            <span>{{ getStatusLabel(status) }}</span>
          </button>
        </div>
      </section>
    </div>

    <div class="empty-state" *ngIf="noData">
      <i class="fa-solid fa-folder-open"></i>
      <h2>لا توجد تقديمات</h2>
      <p>جرب تغيير الفلاتر أو اضغط تحديث لجلب آخر البيانات.</p>
      <button type="button" class="main-btn" (click)="resetFilters()">عرض الكل</button>
    </div>
  </section>
</div>
